import { useEffect, useRef, useState } from 'react';

import {
  DiscordBackfillJobStatusSchema,
  InsightsBroadcastPayloadSchema,
  type DiscordBackfillJobStatus,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import { useToast } from '../toast/use-toast.js';

// ---------------------------------------------------------------------------
// Shell-level job activity: turns background-job realtime into toasts and a
// small live state the relevant pages read for inline status.
//
// Owns the two job subscriptions for the active game so they live in exactly
// one place (per the one-supabase-channel-name-per-app rule). The Insights
// page used to own insights:<gameId>; it now reads insightsVersion from the
// shell instead, and this hook is the sole subscriber.
//
//   1. insights:<gameId>  (broadcast, from notify_insights_snapshot_changed)
//      -> bumps insightsVersion (consumers refetch) and toasts running /
//         done / dead.
//   2. discord_backfill_job (postgres_changes, filtered by game_id)
//      -> toasts running / done / failed and keeps activeBackfillJobs so the
//         watched-channels page can show an inline "Importing history..." row
//         that survives a reload.
//
// Reconnect / visibility handling mirrors use-feedback-inbox-read.ts.
// ---------------------------------------------------------------------------

export interface ActiveBackfillJob {
  id: string;
  status: DiscordBackfillJobStatus;
  channelsTotal: number;
  channelsDone: number;
}

export interface JobActivityHandle {
  insightsVersion: number;
  // True while an insights refresh is in progress for the active game -
  // spanning both the snapshot-collection half and the theme/bucket pass, so
  // it stays on until 'themes_updated'. Hydrated on entry so it survives a
  // reload mid-refresh. Drives the inline "Refreshing insights…" banner.
  insightsRefreshing: boolean;
  // Jobs for the active game that are still pending or running. Cleared as
  // they reach done / failed.
  activeBackfillJobs: ActiveBackfillJob[];
}

interface BackfillRow {
  id: string;
  status: DiscordBackfillJobStatus;
  channelsTotal: number;
  channelsDone: number;
  totalMessages: number;
}

function parseBackfillRow(raw: unknown): BackfillRow | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r['id'] === 'string' ? r['id'] : null;
  if (id === null) return null;
  const status = DiscordBackfillJobStatusSchema.safeParse(r['status']);
  if (!status.success) return null;
  const num = (v: unknown): number => (typeof v === 'number' ? v : 0);
  return {
    id,
    status: status.data,
    channelsTotal: num(r['channels_total']),
    channelsDone: num(r['channels_done']),
    totalMessages: num(r['total_messages']),
  };
}

export function useJobActivity(gameId: string | null): JobActivityHandle {
  const [insightsVersion, setInsightsVersion] = useState(0);
  const [insightsRefreshing, setInsightsRefreshing] = useState(false);
  const [activeBackfillJobs, setActiveBackfillJobs] = useState<ActiveBackfillJob[]>([]);

  const { push, dismiss } = useToast();
  // Hold push / dismiss in refs so the effect doesn't resubscribe when the
  // toast context value changes.
  const pushRef = useRef(push);
  const dismissRef = useRef(dismiss);
  pushRef.current = push;
  dismissRef.current = dismiss;

  // --- Insights broadcast --------------------------------------------------
  useEffect(() => {
    if (gameId === null) {
      setInsightsVersion(0);
      setInsightsRefreshing(false);
      return;
    }
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelay = 1_000;
    let lastStatus: string | null = null;

    // A refresh is two stitched-together jobs: per-channel snapshot collection
    // (running -> done/dead, one per watched channel) and then a per-game
    // theme/bucket pass that ends with a single 'themes_updated'. We treat the
    // whole thing as one job:
    //   - a burst of 'running' collapses into one sticky "Refreshing insights"
    //     toast (with an "N channels" subtitle),
    //   - 'done' is NOT terminal - it's only the collection half, so we just
    //     refetch and keep waiting,
    //   - 'themes_updated' is the real completion: swap the sticky toast for
    //     "Insights updated",
    //   - a pure-failure batch (all 'dead', no 'done') never produces a
    //     themes pass, so a short debounce closes it out as a failure.
    // Buffering also tames the automatic scheduler, which enqueues several
    // channels at once.
    const RUNNING_COALESCE_MS = 700;
    const PURE_FAIL_MS = 1_500;
    // Safety net: if the bot dies mid-theme-pass and 'themes_updated' never
    // arrives, complete the job anyway rather than leaving the sticky toast up
    // forever.
    const THEMES_SAFETY_MS = 60_000;
    let runningCount = 0;
    let doneCount = 0;
    let deadCount = 0;
    let runningTimer: ReturnType<typeof setTimeout> | null = null;
    let pureFailTimer: ReturnType<typeof setTimeout> | null = null;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;
    // The sticky "Refreshing insights" toast id, dismissed when the job ends.
    let runningToastId: string | null = null;

    function channelsLabel(n: number): string {
      return `${n} channel${n === 1 ? '' : 's'}`;
    }

    function clearTimer(t: ReturnType<typeof setTimeout> | null): null {
      if (t !== null) clearTimeout(t);
      return null;
    }

    function flushRunning(): void {
      runningTimer = null;
      const n = runningCount;
      runningCount = 0;
      if (n <= 0) return;
      // Sticky (dismissAfterMs: null): the job spans collection + theme
      // formation, so keep it up until the terminal lands rather than letting
      // it auto-dismiss mid-job.
      if (runningToastId !== null) dismissRef.current(runningToastId);
      runningToastId = pushRef.current({
        tone: 'info',
        title: 'Refreshing insights…',
        dismissAfterMs: null,
        ...(n > 1 ? { body: channelsLabel(n) } : {}),
      });
    }

    function emitTerminal(): void {
      runningTimer = clearTimer(runningTimer);
      pureFailTimer = clearTimer(pureFailTimer);
      safetyTimer = clearTimer(safetyTimer);
      setInsightsRefreshing(false);
      if (runningToastId !== null) {
        dismissRef.current(runningToastId);
        runningToastId = null;
      }
      const done = doneCount;
      const dead = deadCount;
      runningCount = 0;
      doneCount = 0;
      deadCount = 0;
      if (done === 0 && dead === 0) return;
      if (done > 0) {
        pushRef.current({
          tone: 'success',
          title: 'Insights updated',
          ...(done > 1 ? { body: channelsLabel(done) } : {}),
        });
      }
      if (dead > 0) {
        pushRef.current({
          tone: 'danger',
          title: 'Insights refresh failed',
          ...(dead > 1 ? { body: channelsLabel(dead) } : {}),
        });
      }
    }

    function clearCoalesceTimers(): void {
      runningTimer = clearTimer(runningTimer);
      pureFailTimer = clearTimer(pureFailTimer);
      safetyTimer = clearTimer(safetyTimer);
    }

    function teardown(): void {
      if (channel !== null) {
        void supabase.removeChannel(channel);
        channel = null;
      }
    }

    function applyPayload(raw: unknown): void {
      const parsed = InsightsBroadcastPayloadSchema.safeParse(raw);
      if (!parsed.success) return;
      // Any state change is worth a refetch (running flips the in_flight
      // surface; done shows partial results; themes_updated lands final
      // patterns/buckets).
      setInsightsVersion((v) => v + 1);
      switch (parsed.data.status) {
        case 'running':
          setInsightsRefreshing(true);
          runningCount += 1;
          runningTimer = clearTimer(runningTimer);
          runningTimer = setTimeout(flushRunning, RUNNING_COALESCE_MS);
          break;
        case 'done':
          // Collection half only - wait for themes_updated. Arm the safety net
          // in case it never comes. The job stays "refreshing" through the
          // theme/bucket pass.
          setInsightsRefreshing(true);
          doneCount += 1;
          if (safetyTimer === null) safetyTimer = setTimeout(emitTerminal, THEMES_SAFETY_MS);
          break;
        case 'dead':
          // A batch that only ever fails produces no themes pass, so close it
          // out on a short debounce - unless a 'done' is also in the batch, in
          // which case themes_updated will carry the terminal (and the dead
          // count with it).
          deadCount += 1;
          pureFailTimer = clearTimer(pureFailTimer);
          pureFailTimer = setTimeout(() => {
            pureFailTimer = null;
            if (doneCount === 0) emitTerminal();
          }, PURE_FAIL_MS);
          break;
        case 'themes_updated':
          emitTerminal();
          break;
      }
    }

    function connect(): void {
      if (cancelled) return;
      teardown();
      const next = supabase
        .channel(`insights:${gameId}`)
        .on('broadcast', { event: 'snapshot_changed' }, (event) => {
          applyPayload(event.payload);
        });
      channel = next;
      next.subscribe((status) => {
        if (cancelled) return;
        lastStatus = status;
        if (status === 'SUBSCRIBED') {
          reconnectDelay = 1_000;
          if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          return;
        }
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          if (reconnectTimer !== null) return;
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
            connect();
          }, reconnectDelay);
        }
      });
    }

    function onVisibility(): void {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      if (lastStatus !== 'SUBSCRIBED') {
        if (reconnectTimer !== null) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        reconnectDelay = 1_000;
        connect();
      }
    }

    // Hydrate the refreshing flag on entry so a reload mid-refresh still shows
    // the banner. get_insights_refresh_state (0120) covers BOTH halves -
    // in-flight snapshot collection and a running theme/bucket pass - so it
    // holds through the (not-brief) formation phase too, not just collection.
    void supabase
      .rpc('get_insights_refresh_state', { p_game_id: gameId })
      .then(({ data }) => {
        if (cancelled) return;
        if (data === true) setInsightsRefreshing(true);
      });

    document.addEventListener('visibilitychange', onVisibility);
    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      clearCoalesceTimers();
      if (runningToastId !== null) {
        dismissRef.current(runningToastId);
        runningToastId = null;
      }
      document.removeEventListener('visibilitychange', onVisibility);
      teardown();
    };
  }, [gameId]);

  // --- Backfill jobs -------------------------------------------------------
  useEffect(() => {
    if (gameId === null) {
      setActiveBackfillJobs([]);
      return;
    }
    let cancelled = false;
    // Dedupe toasts: one per (jobId, phase). pending->running can both
    // arrive, and we only want a single "started" toast.
    const toasted = new Set<string>();

    function toastOnce(key: string): boolean {
      if (toasted.has(key)) return false;
      toasted.add(key);
      return true;
    }

    function ingest(row: BackfillRow): void {
      if (row.status === 'pending' || row.status === 'running') {
        if (row.status === 'running' && toastOnce(`${row.id}:running`)) {
          pushRef.current({ tone: 'info', title: 'Importing channel history...' });
        }
        setActiveBackfillJobs((current) => {
          const without = current.filter((j) => j.id !== row.id);
          return [
            ...without,
            {
              id: row.id,
              status: row.status,
              channelsTotal: row.channelsTotal,
              channelsDone: row.channelsDone,
            },
          ];
        });
        return;
      }
      // Terminal: drop from active, toast once.
      setActiveBackfillJobs((current) => current.filter((j) => j.id !== row.id));
      if (row.status === 'done' && toastOnce(`${row.id}:done`)) {
        const channels = row.channelsTotal;
        pushRef.current({
          tone: 'success',
          title: 'Channel history imported',
          body: `${row.totalMessages} message${row.totalMessages === 1 ? '' : 's'} from ${channels} channel${channels === 1 ? '' : 's'}`,
        });
      } else if (row.status === 'failed' && toastOnce(`${row.id}:failed`)) {
        pushRef.current({ tone: 'danger', title: 'Channel history import failed' });
      }
    }

    // Hydrate active jobs on entry so a reload mid-import still shows the
    // inline status. We don't toast for these - they're not new transitions.
    void supabase
      .from('discord_backfill_job')
      .select('id, status, channels_total, channels_done, total_messages')
      .eq('game_id', gameId)
      .in('status', ['pending', 'running'])
      .then(({ data }) => {
        if (cancelled || data === null) return;
        const rows: ActiveBackfillJob[] = [];
        for (const raw of data) {
          const parsed = parseBackfillRow(raw);
          if (parsed !== null) {
            rows.push({
              id: parsed.id,
              status: parsed.status,
              channelsTotal: parsed.channelsTotal,
              channelsDone: parsed.channelsDone,
            });
          }
        }
        setActiveBackfillJobs(rows);
      });

    const channel = supabase
      .channel(`backfill-jobs:${gameId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'discord_backfill_job',
          filter: `game_id=eq.${gameId}`,
        },
        (payload) => {
          const row = parseBackfillRow(payload.new);
          if (row !== null) ingest(row);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [gameId]);

  return { insightsVersion, insightsRefreshing, activeBackfillJobs };
}
